import React, { useEffect, useRef } from "react";
import CodeIcon from '@mui/icons-material/Code';
import StorageIcon from '@mui/icons-material/Storage';
import PsychologyIcon from '@mui/icons-material/Psychology';
import '../assets/styles/Expertise.scss';

const techStacks = {
    frontend: ["React", "TypeScript", "JavaScript", "HTML/CSS", "SASS", "Tailwind", "Next.js", "Redux"],
    backend: ["Node.js", "Python", "Flask", "PostgreSQL", "MongoDB", "REST APIs", "Docker", "AWS"],
    ai: ["OpenAI", "LangChain", "LLMs", "RAG", "Vector DBs", "Prompt Engineering", "Streamlit", "HuggingFace"]
};

function Expertise() {
    const sectionRef = useRef(null);

    useEffect(() => {
        const observer = new IntersectionObserver(
            (entries) => {
                entries.forEach((entry) => {
                    if (entry.isIntersecting) {
                        entry.target.classList.add('reveal');
                        observer.unobserve(entry.target);
                    }
                });
            },
            { threshold: 0.15, rootMargin: '0px 0px -50px 0px' }
        );

        const cards = document.querySelectorAll('.skill-card');
        const headers = document.querySelectorAll('#expertise .section-header');
        
        cards.forEach((card) => observer.observe(card));
        headers.forEach((header) => observer.observe(header));

        return () => observer.disconnect();
    }, []);

    return (
        <section className="container" id="expertise" ref={sectionRef}>
            <div className="skills-container">
                <div className="section-header">
                    <span className="subtitle">Areas of Specialization</span>
                    <h2>My <span className="text-gradient">Expertise</span></h2>
                    <div className="header-line"></div>
                </div>

                <div className="skills-grid">
                    {/* Frontend & UI/UX */}
                    <div className="skill-card" style={{ animationDelay: '0.1s' }}>
                        <div className="skill-content">
                            <div className="skill-icon-wrapper">
                                <CodeIcon />
                            </div>
                            <h3>Frontend & UI Engineering</h3>
                            <p>
                                Crafting immersive, high-performance web experiences. 
                                I specialize in modern React ecosystems, focusing on 
                                complex state management, fluid animations, and pixel-perfect 
                                responsive designs that engage users.
                            </p>
                            
                            <div className="tech-stack">
                                <span className="stack-title">Core Technologies</span>
                                <div className="chips-wrapper">
                                    {techStacks.frontend.map((tech, idx) => (
                                        <span key={idx} className="tech-chip">{tech}</span>
                                    ))}
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Backend & Architecture */}
                    <div className="skill-card" style={{ animationDelay: '0.2s' }}>
                        <div className="skill-content">
                            <div className="skill-icon-wrapper">
                                <StorageIcon />
                            </div>
                            <h3>Backend & Architecture</h3>
                            <p>
                                Architecting robust, scalable server-side solutions. 
                                From designing RESTful APIs to optimizing database queries 
                                and orchestrating microservices deployments using Docker 
                                and cloud infrastructure.
                            </p>
                            
                            <div className="tech-stack">
                                <span className="stack-title">Core Technologies</span>
                                <div className="chips-wrapper">
                                    {techStacks.backend.map((tech, idx) => (
                                        <span key={idx} className="tech-chip">{tech}</span>
                                    ))}
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* AI & Machine Learning */}
                    <div className="skill-card" style={{ animationDelay: '0.3s' }}>
                        <div className="skill-content">
                            <div className="skill-icon-wrapper">
                                <PsychologyIcon />
                            </div>
                            <h3>GenAI & LLM Integration</h3>
                            <p>
                                Bridging the gap between traditional software and artificial intelligence. 
                                Building enterprise-grade GenAI solutions, RAG pipelines, and intelligent 
                                agents that automate workflows and enhance decision-making.
                            </p>
                            
                            <div className="tech-stack">
                                <span className="stack-title">Core Technologies</span>
                                <div className="chips-wrapper">
                                    {techStacks.ai.map((tech, idx) => (
                                        <span key={idx} className="tech-chip">{tech}</span>
                                    ))}
                                </div>
                            </div>
                        </div>
                    </div>

                </div>
            </div>
        </section>
    );
}

export default Expertise;
